import { formatDateInTimezone, getTodayInTimezone, toDateKeyInTimezone } from '../utils/dateUtils';
import { nonComplimentaryItems } from '../utils/orderItems';
import { isPlaceholderEmail, formatCurrency } from './customerFormat';

// Shared display rules for the Orders cards and table, so the two views can't
// drift apart on what a name, time window or item line means.

// Link ink, as PageHeader's back link: floral-700, and floral-900 in Forest
// dark, where floral-700 is too dim on the surface. Written out in full so
// Tailwind's scanner sees the literal class names.
export const LINK_INK =
  'text-floral-700 hover:text-floral-800 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:text-floral-800';

export const isPickupOrder = (order) => order.delivery?.fulfillmentType === 'pickup';
export const isDeliveryOrder = (order) => order.delivery?.fulfillmentType === 'delivery';

// Statuses the shop no longer has to act on.
export const SETTLED_STATUSES = ['delivered', 'picked_up', 'cancelled', 'refunded'];
export const isSettled = (order) => SETTLED_STATUSES.includes(order.status);

export const isOpenPickup = (order) => isPickupOrder(order) && !isSettled(order);

const fullName = (p) => `${p?.firstName || ''} ${p?.lastName || ''}`.trim();

export const customerDisplayName = (order) =>
  (order.customer ? fullName(order.customer) : '') || order.customerName || '';

// The person the order is for: the recipient on deliveries, and on pickups the
// person collecting it (which may differ from the customer).
export const recipientDisplayName = (order) =>
  fullName(order.recipient) || customerDisplayName(order);

// Recipient phone first — on pickups that's the pickup contact.
export const recipientPhone = (order) =>
  order.recipient?.phone || order.customer?.phone || order.customerPhone || '';

export const customerPhone = (order) => order.customer?.phone || order.customerPhone || '';

export const customerEmail = (order) => {
  const email = order.customer ? order.customer.email : order.customerEmail;
  return isPlaceholderEmail(email) || (email || '').includes('noemail.local') ? '' : email || '';
};

const addressOf = (order) => order.delivery?.address || order.address || null;

// "106 Strathmore Dr, Apt 2 · Greer". Pickups read "Pickup in store".
export const addressLine = (order) => {
  if (isPickupOrder(order)) return 'Pickup in store';
  const a = addressOf(order);
  if (!a) return '';
  const street = [
    a.street1 || a.street || a.streetAddress || a.address1,
    a.street2 || a.address2,
  ].filter(Boolean).join(', ');
  return [street, a.city].filter(Boolean).join(' · ');
};

// Full address for a title tooltip.
export const fullAddress = (order) => {
  const a = addressOf(order);
  if (!a) return '';
  return [
    order.recipient?.businessName,
    a.street1 || a.street || a.streetAddress || a.address1,
    a.street2 || a.address2,
    [a.city, a.state].filter(Boolean).join(', '),
    a.zip || a.zipCode || a.postalCode,
  ].filter(Boolean).join(' · ');
};

export const orderDateKey = (order) => {
  const d = order.delivery?.date || order.deliveryDate;
  if (!d) return '';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return toDateKeyInTimezone(d) || '';
};

const dayDiff = (a, b) => Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000);

// "Deliver today", "Pickup tomorrow", "Deliver Fri, Oct 3".
export const whenLabel = (order) => {
  const verb = isPickupOrder(order) ? 'Pickup' : 'Deliver';
  const key = orderDateKey(order);
  if (!key) return `${verb} · no date`;
  const diff = dayDiff(key, getTodayInTimezone());
  if (diff === 0) return `${verb} today`;
  if (diff === 1) return `${verb} tomorrow`;
  if (diff === -1) return `${verb} yesterday`;
  return `${verb} ${formatDateInTimezone(key, { weekday: 'short', month: 'short', day: 'numeric' })}`;
};

const clock = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

// The time commitment, if any: "AM", "PM", "by 12:00 PM", "after 2:00 PM",
// "at 3:30 PM". Deliveries carry timeCondition + priorityTime; pickups carry a
// period (morning/afternoon/before/after/exact) + pickupTime.
export const timeWindowLabel = (order) => {
  const d = order.delivery || {};
  const words = { before: 'by', after: 'after', exact: 'at' };
  if (isPickupOrder(order)) {
    const raw = (d.pickupTimePeriod || d.pickupTimePreference || '').toLowerCase();
    if (!raw) return '';
    if (raw === 'morning' || raw === 'am') return 'AM';
    if (raw === 'afternoon' || raw === 'pm') return 'PM';
    if (raw === 'noon') return 'Noon';
    if (words[raw] && d.pickupTime) return `${words[raw]} ${clock(d.pickupTime)}`;
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  if (d.timeCondition === 'am') return 'AM';
  if (d.timeCondition === 'pm') return 'PM';
  if (d.priorityTime) return `${words[d.timeCondition] || 'by'} ${clock(d.priorityTime)}`;
  return '';
};

// Tone of a time-window tag: "by …" is the shop's own commitment (green tint),
// an exact "at …" is the hard one (danger), "after …", AM and PM are neutral.
export const timeWindowTone = (label) => {
  if (/^by /.test(label)) return 'bg-floral-100 text-floral-900';
  if (/^at /.test(label)) return 'bg-danger-bg text-danger-fg';
  return 'bg-neutral-bg text-neutral-fg';
};

// First real item (complimentary gifts don't headline the order) plus how many
// more lines follow.
export const itemSummary = (order) => {
  const items = nonComplimentaryItems(order.items || []);
  if (items.length === 0) return { name: 'No items', qty: 0, more: 0, image: null };
  const first = items[0];
  return {
    name: first.name || first.item_name || 'Item',
    qty: first.quantity > 1 ? first.quantity : 0,
    more: items.length - 1,
    image: items.find((i) => i.image_url)?.image_url || null,
  };
};

// The item line as one string: "2 × Sweet Pea Posy +1 more".
export const itemLine = (order) => {
  const { name, qty, more } = itemSummary(order);
  return `${qty > 0 ? `${qty} × ` : ''}${name}${more > 0 ? ` +${more} more` : ''}`;
};

export const money = (amount) => formatCurrency(amount);

// "(" + total + ")" marks an unpaid order, as the list always has.
export const totalLabel = (order, unpaid) => (unpaid ? `(${money(order.total)})` : money(order.total));

export const locationTypeOf = (order) => {
  const t =
    order.delivery?.locationType ||
    order.delivery?.address?.locationType ||
    order.delivery?.address?.location_type ||
    order.address?.locationType ||
    order.address?.location_type ||
    'residential';
  return t === 'residence' ? 'residential' : t;
};

export const LOCATION_TYPES = [
  { value: 'residential', label: 'Residence' },
  { value: 'business', label: 'Business' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'funeral-home', label: 'Funeral home' },
  { value: 'church', label: 'Church' },
  { value: 'school', label: 'School' },
  { value: 'hotel', label: 'Hotel' },
];
